import { Checkbox, flash, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, getMeta, InputField, requiredNumber, requiredString, SelectField } from "common";
import { FormikProps } from "formik";
import React, { forwardRef, Ref, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Permission, SellerMeta } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { useSecrets, useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import {
    getAutoPrint,
    getDefaultPrinter,
    setAutoPrint,
    setDefaultPrinter,
    USE_COMPUTER_PRINT_DIALOG,
} from "../../../storages/PrinterStorage";
import { getPrinters } from "../../../utils/qzTray";
import { IsGranted } from "../../Common/IsGranted";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { SuffixField } from "../../Field/SuffixField";
import { DebugReceiptPrinting } from "./DebugReceiptPrinting";

interface ReceiptPrintingFormValues {
    defaultPrinter: string;
    autoPrint: boolean;
}

export type ReceiptPrintingFormRef = FormikProps<ReceiptPrintingFormValues>;

export const ReceiptPrintingForm = forwardRef((props, ref: Ref<ReceiptPrintingFormRef>) => {
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const secrets = useSecrets();
    const isDebugMode = window.flags?.debug;

    const { data: printers = [] } = useQuery<string[]>(["printers", secrets], async () => {
        return getPrinters(secrets);
    });

    const handleSubmit = async ({ defaultPrinter, autoPrint }: ReceiptPrintingFormValues) => {
        setDefaultPrinter(defaultPrinter.length > 0 ? defaultPrinter : null);
        setAutoPrint(autoPrint);
        flash.success("Receipt Print Settings updated");
    };

    const initialValues: ReceiptPrintingFormValues = {
        defaultPrinter: getDefaultPrinter() ?? "",
        autoPrint: getAutoPrint(),
    };

    const handleModalClose = () => {
        setShowAdvancedSettings(!showAdvancedSettings);
    };

    return (
        <>
            <XolaFormik enableReinitialize innerRef={ref} initialValues={initialValues} onSubmit={handleSubmit}>
                {({ values, setFieldValue }) => (
                    <div className="space-y-4">
                        <div className="space-y-4">
                            <FormField
                                name="defaultPrinter"
                                label="Receipt Printer"
                                tooltip="Receipts will be sent to this printer after a purchase is completed"
                                className="max-w-96"
                            >
                                <SelectField>
                                    <option value="">Select a printer</option>

                                    {printers.map((printer) => (
                                        <option key={printer} value={printer} className="no-translate">
                                            {printer}
                                        </option>
                                    ))}

                                    <option
                                        key={USE_COMPUTER_PRINT_DIALOG}
                                        value={USE_COMPUTER_PRINT_DIALOG}
                                        className="no-translate"
                                    >
                                        Use computer’s print dialog
                                    </option>
                                </SelectField>
                            </FormField>

                            <Checkbox
                                label="Automatically print receipt"
                                checked={values.autoPrint}
                                onChange={(e: any) => setFieldValue("autoPrint", e.target.checked)}
                            />

                            <SecondaryButton
                                size="small"
                                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                            >
                                Advanced Settings
                            </SecondaryButton>

                            {isDebugMode ? <DebugReceiptPrinting /> : null}
                        </div>
                    </div>
                )}
            </XolaFormik>

            <Modal isOpen={showAdvancedSettings} onClose={handleModalClose}>
                <ModalContent onClose={handleModalClose} />
            </Modal>
        </>
    );
});

const ModalContent = ({ onClose }: { onClose: () => void }) => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { mutateAsync: updateReceiptPreference, isLoading } = useMutation(async (meta: SellerMeta) => {
        return api.post(`/users/${seller.id}/meta`, meta); // This is always the seller ID
    });

    const handleSubmit = async (values: SellerMeta) => {
        await updateReceiptPreference(values);
        invalidate("sellers", seller.id);
        invalidate("users", seller.id);
        flash.success("Receipt Settings updated");
        onClose();
    };

    return (
        <>
            <Modal.Header>Receipt Settings</Modal.Header>

            <XolaFormik initialValues={getMeta(seller)} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ handleSubmit }: any) => {
                    return (
                        <>
                            <Modal.Body>
                                <FormField name="features.pos.html.paddingLeft" label="Left padding">
                                    <SuffixField suffix="Inches">
                                        <InputField
                                            allowZero
                                            type="number"
                                            className="rounded-r-none border-r-0 hover:border-gray-light focus:border-gray-light"
                                        />
                                    </SuffixField>
                                </FormField>

                                <FormField name="features.pos.html.paddingRight" label="Right Padding">
                                    <SuffixField suffix="Inches">
                                        <InputField
                                            allowZero
                                            type="number"
                                            className="rounded-r-none border-r-0 hover:border-gray-light focus:border-gray-light"
                                        />
                                    </SuffixField>
                                </FormField>

                                <FormField name="features.pos.html.paddingBottom" label="Bottom Padding">
                                    <SuffixField suffix="Inches">
                                        <InputField
                                            allowZero
                                            type="number"
                                            className="rounded-r-none border-r-0 hover:border-gray-light focus:border-gray-light"
                                        />
                                    </SuffixField>
                                </FormField>

                                <IsGranted permissions={[Permission.RECEIPT_PRINTING_ADVANCED_EDIT]}>
                                    <FormField name="features.pos.drawerEjectCode" label="Drawer Eject Code">
                                        <InputField />
                                    </FormField>

                                    <FormField name="features.pos.paperCutCode" label="Paper Cut Code">
                                        <InputField />
                                    </FormField>
                                </IsGranted>
                            </Modal.Body>

                            <Modal.Footer className="space-x-4">
                                <SecondaryButton disabled={isLoading} onClick={onClose}>
                                    Cancel
                                </SecondaryButton>

                                <SubmitButton isLoading={isLoading} onClick={handleSubmit}>
                                    Save Changes
                                </SubmitButton>
                            </Modal.Footer>
                        </>
                    );
                }}
            </XolaFormik>
        </>
    );
};

const validationSchema = object().shape({
    features: object().shape({
        pos: object().shape({
            html: object().shape({
                paddingLeft: requiredNumber().min(0),
                paddingRight: requiredNumber().min(0),
                paddingBottom: requiredNumber().min(0),
            }),
            drawerEjectCode: requiredString().trim(),
            paperCutCode: requiredString().trim(),
        }),
    }),
});
